import React, { useState, useEffect } from 'react';
import { Button, Table, Modal, Form, Select, DatePicker, Switch, Input, Popconfirm, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getDelegations, createDelegation, updateDelegation, deleteDelegation } from '../../api/delegations';
import { getBranches } from '../../api/branches';
import BranchSelect from '../common/BranchSelect';
import UserSearchSelect from '../common/UserSearchSelect';
import useDebounce from '../../hooks/useDebounce';
import useAuthStore from '../../store/authStore';
import { apiErrorMessage } from '../../utils/apiError';

const { Option } = Select;
const { RangePicker } = DatePicker;

const ROLE_LABELS = {
  developer_admin: 'Geliştirici Admin',
  super_admin:     'Süper Admin',
  branch_manager:  'Şube Müdürü',
  teacher:         'Öğretmen',
};

const DelegationsTab = ({ mine = false }) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  // Yonetici olmayan roller (ogretmen) sadece kendi adlarina, kendi
  // subelerindeki bir meslektasa vekalet olusturabilir - backend de ayni
  // kurali uyguluyor (DelegationController::store).
  const isTeacher = user?.role === 'teacher';
  // mine: Profil > "Vekaletlerim" sekmesi. Liste caginda backend'e de
  // gonderiliyor - yonetici rolunde kapsami daraltan sey o parametre,
  // buradaki gizleme degil.
  const selfOnly = mine || isTeacher;
  const [delegations, setDelegations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
  const debouncedSearch = useDebounce(search, 400);
  const [form] = Form.useForm();

  useEffect(() => {
    getBranches({ per_page: 100 })
      .then(res => setBranches(res.data.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchDelegations(1);
  }, [debouncedSearch, isActiveFilter, mine]);

  const fetchDelegations = async (page = 1) => {
    setLoading(true);
    try {
      const res = await getDelegations({
        page,
        per_page: pagination.pageSize,
        search:    debouncedSearch || undefined,
        is_active: isActiveFilter !== null ? isActiveFilter : undefined,
        mine:      mine ? 1 : undefined,
      });
      setDelegations(res.data.data || []);
      setPagination(p => ({ ...p, current: page, total: res.data.total }));
    } catch {
      message.error('Vekalet listesi yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    if (selfOnly) {
      form.setFieldsValue({ from_user_id: user.id, branch_id: user.branch_id });
    }
    setModalOpen(true);
  };

  const handleCreate = async (values) => {
    setSaving(true);
    try {
      await createDelegation({
        from_user_id: values.from_user_id,
        to_user_id:   values.to_user_id,
        branch_id:    values.branch_id,
        start_date:   values.date_range[0].format('YYYY-MM-DD'),
        end_date:     values.date_range[1].format('YYYY-MM-DD'),
      });
      message.success('Vekalet oluşturuldu.');
      setModalOpen(false);
      form.resetFields();
      fetchDelegations(1);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Vekalet oluşturulamadı.'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (delegation, checked) => {
    try {
      await updateDelegation(delegation.id, { is_active: checked });
      message.success(checked ? 'Vekalet aktifleştirildi.' : 'Vekalet pasifleştirildi.');
      fetchDelegations(pagination.current);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Durum güncellenemedi.'));
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDelegation(id);
      message.success('Vekalet silindi.');
      fetchDelegations(pagination.current);
    } catch {
      message.error('Silme işlemi başarısız.');
    }
  };

  const columns = [
    {
      title: 'Vekalet Veren',
      key: 'from',
      render: (_, r) => (
        <div>
          {isTeacher ? (
            <span className="font-medium">{r.from_user?.name} {r.from_user?.surname}</span>
          ) : (
            <Button type="link" style={{ padding: 0, height: 'auto' }} className="font-medium" onClick={() => r.from_user && navigate(`/users/${r.from_user.id}`)}>
              {r.from_user?.name} {r.from_user?.surname}
            </Button>
          )}
          <div className="text-xs text-gray-400">{ROLE_LABELS[r.from_user?.role] || r.from_user?.role}</div>
        </div>
      ),
    },
    {
      title: 'Vekalet Alan',
      key: 'to',
      render: (_, r) => (
        <div>
          {isTeacher ? (
            <span className="font-medium">{r.to_user?.name} {r.to_user?.surname}</span>
          ) : (
            <Button type="link" style={{ padding: 0, height: 'auto' }} className="font-medium" onClick={() => r.to_user && navigate(`/users/${r.to_user.id}`)}>
              {r.to_user?.name} {r.to_user?.surname}
            </Button>
          )}
          <div className="text-xs text-gray-400">{ROLE_LABELS[r.to_user?.role] || r.to_user?.role}</div>
        </div>
      ),
    },
    {
      title: 'Şube',
      key: 'branch',
      render: (_, r) => r.branch?.name || '—',
    },
    {
      title: 'Tarih Aralığı',
      key: 'dates',
      render: (_, r) => (
        <span className="text-sm">
          {dayjs(r.start_date).format('DD.MM.YYYY')} — {dayjs(r.end_date).format('DD.MM.YYYY')}
        </span>
      ),
    },
    {
      title: 'Aktif',
      key: 'is_active',
      align: 'center',
      render: (_, r) => (
        <Switch
          checked={r.is_active}
          checkedChildren="Aktif"
          unCheckedChildren="Pasif"
          style={{ backgroundColor: r.is_active ? '#52c41a' : '#ff4d4f' }}
          onChange={(checked) => handleToggleActive(r, checked)}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      align: 'center',
      render: (_, r) => (
        <Popconfirm
          title="Vekalet kalıcı olarak silinsin mi?"
          onConfirm={() => handleDelete(r.id)}
          okText="Evet"
          cancelText="Hayır"
        >
          <Button icon={<DeleteOutlined />} danger size="small" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-3">
          <Input
            placeholder="İsim ara..."
            allowClear
            style={{ width: 220 }}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <Select
            placeholder="Durum"
            allowClear
            style={{ width: 130 }}
            value={isActiveFilter}
            onChange={v => setIsActiveFilter(v ?? null)}
          >
            <Option value={1}>Aktif</Option>
            <Option value={0}>Pasif</Option>
          </Select>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenModal}>
          Yeni Vekalet
        </Button>
      </div>

      <Table
        dataSource={delegations}
        rowKey="id"
        columns={columns}
        loading={loading}
        size="small"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: page => fetchDelegations(page),
          showTotal: total => `Toplam ${total} vekalet`,
        }}
      />

      <Modal
        title="Yeni Vekalet"
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        footer={null}
      >
        <p className="text-gray-500 text-sm mb-4">
          {isTeacher
            ? 'Belirlenen tarih aralığında sizin derslerinizi/öğrencilerinizi seçtiğiniz meslektaşınız da görüp yönetebilecek.'
            : selfOnly
              ? 'Belirlenen tarih aralığında sizin yetkileriniz, seçtiğiniz kullanıcıya devredilir.'
              : 'Belirlenen tarih aralığında vekalet veren kullanıcının yetkileri, vekalet alan kullanıcıya devredilir.'}
        </p>
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          {selfOnly ? (
            <>
              <Form.Item label="Vekalet Veren">
                <Input value={`${user.name} ${user.surname} (Siz)`} disabled />
              </Form.Item>
              <Form.Item name="from_user_id" hidden><Input /></Form.Item>
              <Form.Item name="branch_id" hidden><Input /></Form.Item>
            </>
          ) : (
            <>
              <Form.Item
                name="from_user_id"
                label="Vekalet Veren"
                rules={[{ required: true, message: 'Kullanıcı seçiniz.' }]}
              >
                <UserSearchSelect
                  placeholder="Kullanıcı ara..."
                  renderLabel={(u) => `${u.name} ${u.surname} — ${ROLE_LABELS[u.role] || u.role}`}
                />
              </Form.Item>

              <Form.Item
                name="branch_id"
                label="Şube"
                rules={[{ required: true, message: 'Şube seçiniz.' }]}
              >
                <BranchSelect branches={branches} />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="to_user_id"
            label="Vekalet Alan"
            rules={[{ required: true, message: 'Kullanıcı seçiniz.' }]}
          >
            <UserSearchSelect
              placeholder="Kullanıcı ara..."
              role={isTeacher ? 'teacher' : undefined}
              branchId={isTeacher ? user.branch_id : undefined}
              renderLabel={(u) => `${u.name} ${u.surname} — ${ROLE_LABELS[u.role] || u.role}`}
            />
          </Form.Item>

          <Form.Item
            name="date_range"
            label="Vekalet Tarihi"
            rules={[{ required: true, message: 'Tarih aralığı seçiniz.' }]}
          >
            <RangePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>

          <div className="flex justify-end gap-2">
            <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>İptal</Button>
            <Button type="primary" htmlType="submit" loading={saving}>Kaydet</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default DelegationsTab;
